import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import logger from '../lib/logger';
import { supabase, AuthUserWithProfile } from '../lib/supabase';
import { 
  signInWithEmail, 
  signOut, 
  getCurrentSession, 
  getCurrentUser, 
  getUserProfile, 
  getUserOrganization, 
  getUserRole,
  isEmailVerified,
  refreshSession,
  onAuthStateChange,
  DEMO_ORGANIZATION
} from '../services/authService';

interface UseAuthReturn {
  // State
  user: AuthUserWithProfile | null;
  session: unknown | null;
  profile: ReturnType<typeof getUserProfile> extends Promise<infer T> ? T : never;
  organization: ReturnType<typeof getUserOrganization> extends Promise<infer T> ? T : never;
  role: string;
  
  // Computed
  isAuthenticated: boolean;
  isEmailVerified: boolean;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  signIn: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
  clearError: () => void;
}

/**
 * Hook för att hantera autentisering i CaseFlow+AI
 * 
 * Returnerar:
 * - user: Aktuell användare med profil och organisation
 * - session: Aktuell session
 * - profile: Användarens profil (full_name, role, etc.)
 * - organization: Användarens organisation
 * - role: Användarens roll (admin, technician, agent, viewer)
 * - isAuthenticated: Boolean som indikerar om användaren är inloggad
 * - isEmailVerified: Boolean som indikerar om email är verifierad
 * - isLoading: Boolean som indikerar pågående laddning
 * - signIn: Funktion för att logga in
 * - signOut: Funktion för att logga ut
 * - refreshSession: Funktion för att uppdatera session
 */
export const useAuth = (): UseAuthReturn => {
  // FAS 2: DEV-only debug logging
  if (import.meta.env.DEV) {
    console.log('[DEV AUTH] Hook initialized');
  }
  
  const [user, setUser] = useState<AuthUserWithProfile | null>(null);
  const [session, setSession] = useState<unknown | null>(null);
  const [profile, setProfile] = useState<ReturnType<typeof getUserProfile> extends Promise<infer T> ? T : never>(null);
  const [organization, setOrganization] = useState<ReturnType<typeof getUserOrganization> extends Promise<infer T> ? T : never>(null);
  
  // Ref to track if we have a valid organization (to prevent overwrite to null)
  const hasValidOrgRef = useRef(false);
  // Ref to track user state without causing fetchUserData to be recreated
  const userRef = useRef<AuthUserWithProfile | null>(null);
  const [role, setRole] = useState<string>('viewer');
  const [isEmailVerified, setIsEmailVerified] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Helper to update both user state and ref (prevents stale closures)
  const setUserWithRef = (newUser: AuthUserWithProfile | null) => {
    userRef.current = newUser;
    setUser(newUser);
  };

  // Hämta användardata (profil + organisation)
  const fetchUserData = useCallback(async (userId: string, existingUser?: AuthUserWithProfile) => {
    try {
      setIsLoading(true);
      
      // FAS 3 & 4: For demo users, NORMALIZE auth shape and NEVER overwrite organization to null
      const isDemoUser = userId.startsWith('demo-');
      
      if (isDemoUser && import.meta.env.DEV) {
        // FAS 4: For demo users, FORCE set DEMO_ORGANIZATION
        // This ensures organization is NEVER null for demo users
        const demoUserWithOrg = {
          ...(existingUser || userRef.current),
          organization_id: DEMO_ORGANIZATION.id,
          organization: DEMO_ORGANIZATION,
        };
        
        setOrganization(DEMO_ORGANIZATION);
        setUserWithRef(demoUserWithOrg as AuthUserWithProfile);
        hasValidOrgRef.current = true;
        
        // Still fetch profile for role information
        const profileData = await getUserProfile(userId);
        setProfile(profileData as never);
        
        const userRole = profileData?.role || 'viewer';
        setRole(userRole);
        
        setIsEmailVerified(true);
        
        // FAS 2: DEV-only debug
        if (import.meta.env.DEV) {
          console.log('[DEV AUTH] fetchUserData DEMO', {
            user: demoUserWithOrg,
            organization: DEMO_ORGANIZATION,
            organizationId: DEMO_ORGANIZATION.id,
            userOrganization: demoUserWithOrg.organization,
            organizationIdFromUser: demoUserWithOrg.organization_id,
            role: userRole,
            isAuthenticated: true,
          });
        }
        
        logger.audit('Demo användardata hämtad', { 
          userId: userId.substring(0, 8) + '...', 
          role: userRole,
          hasOrganization: true,
          isDemo: true,
        });
        
        setIsLoading(false);
        return;
      }
      
      // Normal flow for non-demo users
      const [profileData, organizationData, emailVerified] = await Promise.all([
        getUserProfile(userId),
        getUserOrganization(userId),
        isEmailVerified(userId),
      ]);

      setProfile(profileData as never);
      
      // Don't overwrite organization to null if we already have a valid one
      if (organizationData) {
        setOrganization(organizationData as never);
        hasValidOrgRef.current = true;
      } else if (!hasValidOrgRef.current) {
        setOrganization(null as never);
      }
      
      setIsEmailVerified(emailVerified);
      
      // FAS 2: DEV-only debug
      if (import.meta.env.DEV) {
        console.log('[DEV AUTH] fetchUserData', {
          userId: userId.substring(0, 8) + '...',
          hasOrganization: !!organizationData,
          organizationId: organizationData?.id,
          isDemoUser,
        });
      }
      
      // Sätt roll från profil eller default 'viewer'
      const userRole = profileData?.role || 'viewer';
      setRole(userRole);
      
      // Update user object with organization if not already set
      if (existingUser && organizationData && !existingUser.organization) {
        setUserWithRef({
          ...existingUser,
          organization: organizationData,
        });
      }
      
      logger.audit('Användardata hämtad', {
        userId: userId.substring(0, 8) + '...',
        role: userRole,
        hasOrganization: !!organizationData,
        isEmailVerified: emailVerified,
      });
    } catch (err) {
      logger.error('Fel vid hämtning av användardata', { error: String(err) });
      setRole('viewer'); // Säker default
      setIsEmailVerified(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initiera auth state
  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Hämta aktuell session
        const currentSession = await getCurrentSession();
        
        if (!mounted) return;

        if (currentSession) {
          setSession(currentSession);
           
         // Hämta användare
         const currentUser = await getCurrentUser();
           
         if (currentUser) {
           const authUser: AuthUserWithProfile = {
             id: currentUser.id,
             email: currentUser.email || '',
             organization: currentUser.organization,
             profile: currentUser.profile,
             email_verified: currentUser.email_verified,
           };
            
           setUserWithRef(authUser);
            
            // FAS 2: DEV-only debug
            if (import.meta.env.DEV) {
              console.log('[DEV AUTH] initializeAuth', {
                user: authUser,
                organization: authUser.organization,
                userId: authUser.id?.substring(0, 8) + '...',
              });
            }
            
            // Hämta användardata
            await fetchUserData(currentUser.id, authUser);
          }
        } else {
          setUserWithRef(null);
          setProfile(null as never);
          setOrganization(null as never);
          setRole('viewer');
          setIsEmailVerified(false);
          setIsLoading(false);
        }
      } catch (err) {
        if (!mounted) return;
        
        logger.error('Fel vid initiering av auth', { error: String(err) });
        setError('Kunde inte hämta inloggningsstatus');
        setIsLoading(false);
      }
    };

    initializeAuth();

    // Lyssna på auth-state ändringar
    const { data: { subscription } } = onAuthStateChange(async (event, session) => {
      if (!mounted) return;
      
      logger.audit('Auth state change', { event, hasSession: !!session });
      
      if (session) {
        setSession(session);
         
        // Extract user from session
        const sessionUser = (session as any)?.user;
        if (sessionUser) {
          const authUser: AuthUserWithProfile = {
            id: sessionUser.id,
            email: sessionUser.email || '',
            organization: sessionUser.organization,
            profile: sessionUser.profile,
            email_verified: sessionUser.email_verified,
          };
           
          setUserWithRef(authUser);
          await fetchUserData(sessionUser.id, authUser);
        } else if (supabase) {
          // Only try to get user if supabase is configured
          const { data: { user: currentUser } } = await supabase.auth.getUser();
           
          if (currentUser) {
            const authUser: AuthUserWithProfile = {
              id: currentUser.id,
              email: currentUser.email || '',
            };
             
            setUserWithRef(authUser);
            await fetchUserData(currentUser.id);
          }
        } else {
          // Supabase not configured, just set session without user details
          logger.info('Supabase inte konfigurerat, kan inte hämta användardetaljer', {});
        }
      } else {
        // FAS 4: Only clear organization if NOT a demo user
        // Check if current user is demo before clearing
        if (user && user.id?.startsWith('demo-')) {
          // Don't clear demo user data - keep organization
          logger.info('Demo user session ended, preserving demo state', {});
          return;
        }
        
        setUserWithRef(null);
        setSession(null);
        setProfile(null as never);
        setOrganization(null as never);
        setRole('viewer');
        setIsEmailVerified(false);
        setIsLoading(false);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [fetchUserData]);

  // Sign in funktion
  const handleSignIn = useCallback(async (email: string, password: string) => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await signInWithEmail(email, password);
      
      if (result.success && result.user) {
        setUserWithRef(result.user);
        setSession(await getCurrentSession());
        
        // FAS 3: For demo users, ensure organization is set correctly
        const isDemoUser = result.user.id?.startsWith('demo-');
        if (isDemoUser && import.meta.env.DEV) {
          // Force set DEMO_ORGANIZATION for demo users
          const userWithOrg = {
            ...result.user,
            organization_id: DEMO_ORGANIZATION.id,
            organization: DEMO_ORGANIZATION,
          };
          setUserWithRef(userWithOrg as AuthUserWithProfile);
          setOrganization(DEMO_ORGANIZATION);
          hasValidOrgRef.current = true;
        } else if (result.user.organization) {
          setOrganization(result.user.organization);
        }
        
        // Hämta användardata
        if (result.user.id) {
          await fetchUserData(result.user.id, result.user);
        }
        
        // Set RLS context for tenant isolation (critical for RLS policies)
        if (result.user.organization_id && result.user.profile?.role) {
          try {
            await supabase.rpc('set_rls_context', {
              p_organization_id: result.user.organization_id,
              p_role: result.user.profile.role,
            });
            logger.audit('RLS context set after login', { 
              organizationId: result.user.organization_id,
              role: result.user.profile.role,
            });
          } catch (err) {
            logger.warn('Could not set RLS context after login', { error: String(err) });
          }
        }
        
        return { success: true };
      } else {
        setError(result.error || 'Inloggning misslyckades');
        setIsLoading(false);
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMsg = 'Oväntat fel vid inloggning';
      logger.error(errorMsg, { error: String(err) });
      setError(errorMsg);
      setIsLoading(false);
      return { success: false, error: errorMsg };
    }
  }, [fetchUserData]);

  // Sign out funktion
  const handleSignOut = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      await signOut();
      
      setUserWithRef(null);
      setSession(null);
      setProfile(null as never);
      setOrganization(null as never);
      setRole('viewer');
      setIsEmailVerified(false);
      hasValidOrgRef.current = false;
      
      logger.audit('Användare utloggad via useAuth', {});
    } catch (err) {
      logger.error('Fel vid utloggning', { error: String(err) });
      setError('Kunde inte logga ut');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Refresh session funktion
  const handleRefreshSession = useCallback(async () => {
    try {
      setIsLoading(true);
      const currentSession = await refreshSession();
       
      if (currentSession) {
        setSession(currentSession);
          
        const currentUser = await getCurrentUser();
        if (currentUser) {
          const authUser: AuthUserWithProfile = {
            id: currentUser.id,
            email: currentUser.email || '',
            organization: currentUser.organization,
            profile: currentUser.profile,
            email_verified: currentUser.email_verified,
          };
          setUserWithRef(authUser);
          await fetchUserData(currentUser.id, authUser);
        }
      }
    } catch (err) {
      logger.error('Fel vid uppdatering av session', { error: String(err) });
    } finally {
      setIsLoading(false);
    }
  }, [fetchUserData]);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Computed values
  const isAuthenticated = useMemo(() => {
    return !!user && !!session;
  }, [user, session]);

  return {
    // State
    user,
    session,
    profile,
    organization,
    role,
    
    // Computed
    isAuthenticated,
    isEmailVerified,
    isLoading,
    error,
    
    // Actions
    signIn: handleSignIn,
    signOut: handleSignOut,
    refreshSession: handleRefreshSession,
    clearError,
  };
};

export default useAuth;
